// Warden meshes: a brass carapace, a dark iris ring, and one aether eye —
// the machine IS the gauge. The eye is amber while it merely watches, red
// while it hunts, and dark while it hangs adrift; the carapace dims by
// integrity band. No bars, no numbers, nothing floating over it.

import * as THREE from "three";
import type { World } from "../core/world";
import { cloneAuthoredAsset, disposeAuthoredObject, getAuthoredTemplate } from "./authored-assets";
import { Tracker } from "./common";

const EYE_LOOK: Record<string, { color: number; intensity: number }> = {
  dormant: { color: 0xd8b04a, intensity: 1.2 },
  hunting: { color: 0xe0524a, intensity: 2.6 },
  adrift: { color: 0x22201c, intensity: 0.05 },
};

/** Carapace brightness by integrity band — scoring reads at a glance. */
const HULL_SHADE: Record<string, number> = {
  whole: 1,
  scored: 0.8,
  riven: 0.55,
  adrift: 0.35,
};

interface WardenEntry {
  root: THREE.Group;
  eyes: THREE.MeshStandardMaterial[];
  hulls: THREE.MeshStandardMaterial[];
  authored: boolean;
  tumblePhase: number;
}

interface SharedParts {
  carapace: THREE.BufferGeometry;
  ring: THREE.BufferGeometry;
  eye: THREE.BufferGeometry;
  vane: THREE.BufferGeometry;
  dark: THREE.Material;
}

export class WardenLayer {
  readonly group = new THREE.Group();
  private tracker = new Tracker();
  private parts: SharedParts;
  private entries = new Map<string, WardenEntry>();
  private elapsed = 0;

  constructor() {
    const t = this.tracker;
    this.parts = {
      carapace: t.geo(new THREE.IcosahedronGeometry(4.2, 1)),
      ring: t.geo(new THREE.TorusGeometry(5.4, 0.5, 8, 24)),
      eye: t.geo(new THREE.SphereGeometry(1.4, 12, 10)),
      vane: t.geo(new THREE.BoxGeometry(0.5, 7.5, 0.5)),
      dark: t.mat(new THREE.MeshStandardMaterial({ color: 0x14131a, roughness: 0.85 })),
    };
  }

  sync(world: World, dt: number): void {
    this.elapsed += dt;

    for (const [id, entry] of this.entries) {
      if (!world.wardens.has(id)) {
        this.group.remove(entry.root);
        if (entry.authored) disposeAuthoredObject(entry.root);
        else {
          for (const material of [...entry.eyes, ...entry.hulls]) material.dispose();
        }
        this.entries.delete(id);
      }
    }

    for (const [id, warden] of world.wardens) {
      let entry = this.entries.get(id);
      if (!entry) {
        const hull = new THREE.MeshStandardMaterial({
          color: 0x8a6a3a,
          metalness: 0.7,
          roughness: 0.42,
        });
        const eye = new THREE.MeshStandardMaterial({
          color: 0xd8b04a,
          emissive: 0xd8b04a,
          emissiveIntensity: 1.2,
        });
        const authored = getAuthoredTemplate("orbital-warden") !== undefined;
        const root = authored ? cloneAuthoredAsset("orbital-warden") : new THREE.Group();
        const eyes = new Set<THREE.MeshStandardMaterial>();
        const hulls = new Set<THREE.MeshStandardMaterial>();
        if (authored) {
          root.traverse((node) => {
            if (!(node instanceof THREE.Mesh)) return;
            for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
              if (!(material instanceof THREE.MeshStandardMaterial)) continue;
              if (material.name === "fauna-eye") eyes.add(material);
              else if (material.name === "fauna-hull") hulls.add(material);
            }
          });
          eye.dispose();
          hull.dispose();
        } else {
          eyes.add(eye);
          hulls.add(hull);
          root.add(new THREE.Mesh(this.parts.carapace, hull));
          const ring = new THREE.Mesh(this.parts.ring, this.parts.dark);
          ring.rotation.x = Math.PI / 2;
          root.add(ring);
          const eyeMesh = new THREE.Mesh(this.parts.eye, eye);
          eyeMesh.position.set(0, 0, 3.6);
          root.add(eyeMesh);
          for (const angle of [0.6, 2.7, 4.4]) {
            const vane = new THREE.Mesh(this.parts.vane, this.parts.dark);
            vane.position.set(Math.cos(angle) * 4.6, Math.sin(angle) * 1.2, -1.5);
            vane.rotation.z = angle;
            root.add(vane);
          }
        }
        entry = {
          root,
          eyes: [...eyes],
          hulls: [...hulls],
          authored,
          tumblePhase: (id.length * 1.7 + id.charCodeAt(id.length - 1)) % (Math.PI * 2),
        };
        this.entries.set(id, entry);
        this.group.add(entry.root);
      }

      const pose = warden.renderPose;
      entry.root.position.set(pose.x, pose.y, pose.z);
      if (warden.state === "adrift") {
        // A dead machine tumbles, slowly.
        entry.root.rotation.set(
          Math.sin(this.elapsed * 0.21 + entry.tumblePhase) * 0.7,
          entry.root.rotation.y + dt * 0.13,
          Math.cos(this.elapsed * 0.17 + entry.tumblePhase) * 0.5,
        );
      } else {
        entry.root.rotation.set(0, pose.yaw, 0);
      }

      const look = EYE_LOOK[warden.state] ?? EYE_LOOK.dormant;
      if (look) {
        const pulse = warden.state === "hunting" ? Math.sin(this.elapsed * 9) * 0.6 : 0;
        for (const eye of entry.eyes) {
          eye.color.setHex(look.color);
          eye.emissive.setHex(look.color);
          eye.emissiveIntensity = look.intensity + pulse;
        }
      }
      const shade = HULL_SHADE[warden.band] ?? 1;
      for (const hull of entry.hulls) {
        // Authored brass already has colour in its map; modulate that texture
        // with a neutral wear multiplier instead of tinting it twice.
        if (entry.authored) hull.color.setScalar(shade);
        else hull.color.setScalar(0).offsetHSL(0.09, 0.42, 0.18 * shade + 0.1);
      }
    }
  }

  dispose(): void {
    for (const entry of this.entries.values()) {
      this.group.remove(entry.root);
      if (entry.authored) disposeAuthoredObject(entry.root);
      else {
        for (const material of [...entry.eyes, ...entry.hulls]) material.dispose();
      }
    }
    this.entries.clear();
    this.tracker.dispose();
  }
}
